import React, { useState } from 'react';
import { Bed, Bath, Maximize2, Users, ZoomIn } from 'lucide-react';
import ImageLightbox from './ImageLightbox';

const ArchitectureSection = ({ content }) => {
  const [lightboxImages, setLightboxImages] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);

  const openLightbox = (images, index) => {
    setLightboxImages(images);
    setLightboxIndex(index);
  };

  const closeLightbox = () => {
    setLightboxIndex(null);
  };

  if (!content) return null;

  return (
    <section id="architecture" className="py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            {content.title}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-blue-600 to-amber-500 mx-auto rounded-full" />
        </div>

        {/* Properties */}
        <div className="space-y-24">
          {content.properties.map((property, propIndex) => (
            <div
              key={property.id}
              className={`grid lg:grid-cols-2 gap-12 items-center ${
                propIndex % 2 === 1 ? 'lg:flex-row-reverse' : ''
              }`}
            >
              {/* Image Gallery */}
              <div
                className={`relative ${
                  propIndex % 2 === 1 ? 'lg:order-2' : ''
                }`}
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl group">
                  <img
                    src={property.image}
                    alt={property.name}
                    className="w-full h-full object-cover aspect-[4/3] transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                  <button
                    onClick={() => openLightbox(property.gallery, 0)}
                    className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-white/90 hover:bg-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 hover:scale-110"
                    aria-label="View gallery"
                  >
                    <ZoomIn className="w-8 h-8 text-slate-900" />
                  </button>
                </div>

                {/* Thumbnail Gallery */}
                {property.gallery.length > 1 && (
                  <div className="grid grid-cols-3 md:grid-cols-4 gap-4 mt-4">
                    {property.gallery.map((img, idx) => (
                      <div
                        key={idx}
                        onClick={() => openLightbox(property.gallery, idx)}
                        className="relative aspect-video rounded-lg overflow-hidden cursor-pointer group"
                      >
                        <img
                          src={img}
                          alt={`${property.name} ${idx + 1}`}
                          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors duration-300 flex items-center justify-center">
                          <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Property Details */}
              <div
                className={`space-y-6 ${
                  propIndex % 2 === 1 ? 'lg:order-1' : ''
                }`}
              >
                <div>
                  <p className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">
                    {property.tagline}
                  </p>
                  <h3 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
                    {property.name}
                  </h3>
                  <p className="text-lg text-slate-600 leading-relaxed">
                    {property.description}
                  </p>
                </div>

                {/* Specs */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <Bed className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Bedrooms</p>
                      <p className="text-lg font-bold text-slate-900">
                        {property.specs.bedrooms}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <Bath className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Bathrooms</p>
                      <p className="text-lg font-bold text-slate-900">
                        {property.specs.bathrooms}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <Maximize2 className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Area</p>
                      <p className="text-lg font-bold text-slate-900">
                        {property.specs.area}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-slate-50 rounded-lg p-4 border border-slate-200">
                    <Users className="w-6 h-6 text-blue-600" />
                    <div>
                      <p className="text-sm text-slate-600">Capacity</p>
                      <p className="text-lg font-bold text-slate-900">
                        {property.specs.capacity}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Features List */}
                <div>
                  <h4 className="text-xl font-bold text-slate-900 mb-4">
                    Key Features
                  </h4>
                  <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {property.features.map((feature, index) => (
                      <li
                        key={index}
                        className="flex items-start gap-2 text-slate-700"
                      >
                        <span className="text-blue-600 mt-1">•</span>
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Lightbox */}
      <ImageLightbox
        images={lightboxImages}
        currentIndex={lightboxIndex}
        onClose={closeLightbox}
        onNavigate={setLightboxIndex}
      />
    </section>
  );
};

export default ArchitectureSection;
